// ============================================================
// MongoDBAggregation.jsx
//
// A branded, technical guide to the MongoDB Aggregation
// Framework: Pipelines, Stages, and Accumulators.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Secondary   → #10B981  (Emerald / Analytics)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Button,
    Tooltip
} from "@mui/material";

/* MUI Icons */
import FilterListIcon from "@mui/icons-material/FilterList";
import FunctionsIcon from "@mui/icons-material/Functions";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import TerminalIcon from "@mui/icons-material/Terminal";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import InsightsIcon from "@mui/icons-material/Insights";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    green: "#00ED64",
    emerald: "#10B981",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.green }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#D1D5DB", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBAggregation() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.green }} />}
                        label="Data Analytics · Module 10"
                        sx={{ bgcolor: "rgba(0,237,100,0.1)", color: BRAND.green, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        The <span style={{ color: BRAND.green }}>Aggregation</span> Pipeline
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Master the power of the Pipeline. Aggregation operations process data records and return
                        computed results. Use it to group data, perform lookups across collections, and
                        calculate real-time business metrics.
                    </Typography>
                </Box>

                {/* 2. PIPELINE CONCEPT */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <InsightsIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>The Pipeline Concept</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4, lineHeight: 1.8 }}>
                        Think of aggregation as an <strong>Assembly Line</strong>. Documents enter the pipeline,
                        and each "stage" performs a specific operation (Filtering, Sorting, Grouping)
                        before passing the modified data to the next stage.
                    </Typography>



                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        {[
                            { stage: "$match", desc: "Filters documents (like 'WHERE' in SQL)." },
                            { stage: "$group", desc: "Groups documents by a key and calculates totals." },
                            { stage: "$project", desc: "Reshapes documents (Selects specific fields)." },
                            { stage: "$sort", desc: "Orders the resulting documents." }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Paper sx={{ p: 2.5, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}`, height: '100%' }}>
                                    <Typography variant="h6" sx={{ color: BRAND.green, fontWeight: 800 }}>{item.stage}</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. PRACTICAL EXAMPLE */}
                <Grid container spacing={6} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <QueryStatsIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Analytics in Action</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            In this example, we find all "Completed" orders, group them by customer,
                            and calculate the total amount spent by each person.
                        </Typography>
                        <Stack spacing={2}>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }}>
                                <Typography variant="caption" sx={{ color: BRAND.green }}>Step 1: $match</Typography>
                                <Typography variant="body2">Find only 'Completed' orders.</Typography>
                            </Box>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }}>
                                <Typography variant="caption" sx={{ color: BRAND.green }}>Step 2: $group</Typography>
                                <Typography variant="body2">Sum up the 'total' field per customer.</Typography>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <CodeTerminal
                            label="mongosh: Sales Pipeline"
                            code={`db.orders.aggregate([\n  { $match: { status: "Completed" } },\n  { \n    $group: {\n      _id: "$customerId", \n      totalSpent: { $sum: "$amount" },\n      orderCount: { $count: {} }\n    } \n  },\n  { $sort: { totalSpent: -1 } }\n]);`}
                        />
                    </Grid>
                </Grid>



                {/* 4. ADVANCED STAGES */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <ViewModuleIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Advanced Pipeline Stages</Typography>
                    </Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 4, bgcolor: BRAND.surface, borderRadius: 4, border: `1px solid ${BRAND.border}` }}>
                                <Typography variant="h6" sx={{ color: BRAND.green, mb: 1 }}>$lookup (The NoSQL Join)</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mb: 2 }}>
                                    Used to perform a "left outer join" to another collection in the same database.
                                </Typography>
                                <CodeTerminal label="Lookup Example" code={`{ \n  $lookup: {\n    from: "users",\n    localField: "userId",\n    foreignField: "_id",\n    as: "userDetails"\n  } \n}`} />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 4, bgcolor: BRAND.surface, borderRadius: 4, border: `1px solid ${BRAND.border}` }}>
                                <Typography variant="h6" sx={{ color: BRAND.green, mb: 1 }}>$unwind</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mb: 2 }}>
                                    Deconstructs an array field from the input documents to output a document for each element.
                                </Typography>
                                <CodeTerminal label="Unwind Example" code={`{ $unwind: "$tags" }`} />
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* 5. PRO-TIP: PERFORMANCE */}
                <Box sx={{ mb: 10 }}>
                    <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                        <Stack direction="row" spacing={2}>
                            <Avatar sx={{ bgcolor: BRAND.green }}><TerminalIcon /></Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>The Early Match Rule</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                    Always try to place <code>$match</code> and <code>$sort</code> stages at the <strong>very beginning</strong> of your pipeline.
                                    This allows MongoDB to use <strong>Indexes</strong> to filter the data, significantly reducing the amount of
                                    memory and time needed for subsequent stages.
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <FunctionsIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Data Factory Ready?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Now that you can calculate complex metrics, let's learn how to optimize these
                        queries for production speed using Indexing.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900 }}>Practice Pipelines</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Operator Reference</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
